import { randomThemeScreenshot } from '../../../util/cvd-image';
import { randomThemeContrastPair } from '../../../util/contrast-pair';
import { DOCK_TOWN_SHOWCASE } from '../../../data/product/dock-town-features.data';
import { toolLine } from 'src/app/util/tool-line';
import { LANGMI_SHOWCASE } from '../../../data/product/langmi-features.data';
import { TIDY_DESKTOP_SHOWCASE } from '../../../data/product/tidy-desktop-features.data';
import { AI_TOKEN_METER_SHOWCASE } from '../../../data/product/ai-token-meter-features.data';
import { COLOR_PICKERS_SHOWCASE } from '../../../data/product/color-pickers.data';
import { SERVICES_SHOWCASE } from '../../../data/product/services-showcase.data';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterContentInit, Component, DoCheck, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HeroBoard, HeroMotif, heroMotifFor } from '../../../data/hero-motif/hero-motif.data';
import { ProductShowcaseComponent } from '../../../component/product/product-showcase/product-showcase.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { forkJoin, merge, of } from 'rxjs';
import { catchError, debounceTime, skip, tap } from 'rxjs/operators';
import { BLOG } from 'src/app/data/blog/blog.data';
import { Blog } from 'src/app/model/blog/blog.model';
import { TESTIMONIALS } from 'src/app/data/testimonial/testimonial.data';
import { Review } from 'src/app/model/review/review.model';
import { environment } from '../../../../environments/environment';
import { environmentCommon } from '../../../../environments/environment-common';
import { BRAND } from '../../../data/brand/brand.data';
import { URLS } from '../../../data/navigation/navigation.data';
import { SERVICES, THEMES, TOOLS } from '../../../data/product/product.data';
import { DarkLightSettings } from '../../../data/theme/theme.data';
import { combinedRating, Product, ProductLine, platformCtaLabel, platformCtaKind, isThemeFree, isPlatformPlanned, sortPlatformsByCoverage, platformSku, platformListPrice, freeStoreOfferLabel, platformCtaLabels } from '../../../model/product/product.model';
import { StoreService } from '../../../service/store/store.service';
import { marketplaceReviewCards, storeReviewCards } from '../../../util/review-cards';
import { DirectDownloadService } from '../../../service/store/direct-download.service';
import { CartPopupService } from '../../../service/cart/cart-popup.service';
import { FlatPlatform } from '../../../model/product/flat-platform.model';
import { HEX_CODES } from '../../../data/color/color.data';
import { ColorService } from '../../../service/color/color.service';
import { GRADIENT_SCHEME_MINS, PALETTE_SCHEME_MINS, PaletteGenerationService } from '../../../service/palette/palette-generation.service';
import { PaletteSubtitle } from '../../../component/palette-widget/palette-widget.component';
import { ToolSlide } from '../../../component/product/tools-showcase/tools-showcase.component';
import { AiCostCardChange } from '../../../component/ai-cost-widget/ai-cost-widget.component';
import { AiWatchBoardChange } from '../../../component/ai-price-watch-widget/ai-price-watch-widget.component';
import { AI_TOOLS } from '../../../data/ai/ai-tools.data';
import { AI_COST_DEFAULTS, AiCostValues, aiCostQueryParams } from '../../../util/ai-cost';
import { AI_WATCH_DEFAULT_FIELD, AI_WATCH_DEFAULT_RANGE, AiWatchField, AiWatchRange } from '../../../util/ai-price-watch';
import { WALLPAPER_STYLES, WallpaperService, WallpaperStyle, defaultParams, paramsAreDefault } from '../../../service/wallpaper/wallpaper.service';
import { DynamicWallpaperOptions, DynamicWallpaperService } from '../../../service/wallpaper/dynamic/dynamic-wallpaper.service';
import { DEFAULT_DAY_LIGHT, DYNAMIC_MODES, DynamicMode, FRAME_COUNTS } from '../../../service/wallpaper/dynamic/day-light';
import { bandOrder, composeBands, distinctLooks } from '../../../service/wallpaper/dynamic/day-bands';
import { DynamicSettings } from '../../../component/wallpaper-widget/wallpaper-widget.component';
import { CodeImageState } from '../../../service/code-image/code-image.state';
import { CODE_IMAGE_THEMES } from '../../../data/theme/code-image-themes.data';
import { MonitorProviderService } from '../../../service/provider/monitor-provider.service';
import { RoutingService } from '../../../service/routing/routing.service';
import { ThemeService } from '../../../service/theme/theme.service';
import { UtilService } from '../../../service/util/util.service';
import { AnalyticsService } from '../../../service/analytics/analytics.service';

// The hero's total-downloads count-up (ms).
const HERO_COUNTER_DURATION_MS = 3000; // a second shorter again (owner, 2026-10-11); the board storms while it counts

/** the themes whose hero counter takes the primary colour, not the circle's most colourful one (owner, 2026-10-28) */
const HERO_COUNTER_PRIMARY = [ 'fuji' ];
/** a hero counter colour set by hand where the scene runs under the number (owner, 2026-10-28: Rio's blue sat on
 *  the sea, a navy instead; Sydney its Ocean Ink; Dublin a dark green; Tallinn's blue and Madrid's red a
 *  shade deeper; Aurora the violet of its circle) */
const HERO_COUNTER_FIXED: { [ key: string ]: string } = {
  'aurora-borealis': '#BAA5FF',
  'rio-de-janeiro': '#2A4485',
  'sydney': '#062E3A',
  'dublin': '#0B5A38',
  'tallinn': '#24508C',
  'madrid': '#A23A36',
};

@Component( {
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: [ './home-page.component.scss' ],
} )
export class HomePageComponent implements AfterContentInit, DoCheck, OnInit, OnDestroy {

  @ViewChild( 'globe', { static: true } ) globe: ElementRef | undefined;
  // the hero's board (the theme's motif, the circuit board by default): charged while the downloads counter
  // counts (owner, 2026-10-10). It is swapped when the theme changes, so the new one takes the charge as it comes.
  @ViewChild( 'circuit' ) set circuit( board: HeroBoard | undefined ) {
    this.board = board;
    if ( board && this.boardCharged ) { board.charge( true ); }
  }
  get circuit(): HeroBoard | undefined {
    return this.board;
  }
  private board: HeroBoard | undefined;
  private boardCharged = false;
  // which board the hero draws: the selected theme's own motif (owner, 2026-10-16)
  heroMotif: HeroMotif = 'circuit';
  heroPalette: string[] | null = null;
  @ViewChild( 'heroCounterEl' ) heroCounterRef: ElementRef<HTMLElement> | undefined;
  private motifSub: Subscription | undefined;
  private heroThemeTokens: any = null;
  private heroInputBackground: string | null = null;
  private heroThemeKey: string | null = null;
  private surfaceSub: Subscription | undefined;
  private heroCounterColorTimer: ReturnType<typeof setTimeout> | undefined;
  private circuitChargeCap: ReturnType<typeof setTimeout> | undefined;
  // the Our Services box, turned to the service a footer link names (#services-<key>)
  @ViewChild( 'servicesBox' ) servicesBox: ProductShowcaseComponent | undefined;
  private fragmentSub: Subscription | undefined;
  private appearanceSub: Subscription | undefined;

  animationInterval=16000;

  BRAND = BRAND;
  URLS = URLS;

  // Live palette-tool widget (a teaser of the full tool) shown in Color Tools.
  paletteColors: { value: string }[] = [];
  paletteSubtitle: PaletteSubtitle = { kind: '' };
  paletteCount = 10;

  // Live gradient-tool widget (same teaser pattern as the palette widget).
  gradientColors: { value: string }[] = [];
  gradientSubtitle: PaletteSubtitle = { kind: '' };
  gradientType: 'linear' | 'radial' | 'conic' = 'linear';
  gradientAngle = 115;
  gradientCount = 10;
  gradientPositions: number[] = [];
  gradientSource: { theme?: string; scheme?: string; initializer?: string; seed?: string } = {};

  // Live wallpaper-tool widget (the same teaser pattern; 2026-09-22).
  wallpaperColors: { value: string }[] = [];
  wallpaperSubtitle: PaletteSubtitle = { kind: '' };
  wallpaperStyle: WallpaperStyle = 'mesh';
  wallpaperSizeKey = 'fhd';
  wallpaperSeed = 1;
  wallpaperParams: number[] = defaultParams( 'mesh' );
  wallpaperCount = 6;
  private wallpaperSource: { theme?: string; scheme?: string; initializer?: string; seed?: string } = {};

  // The generator each home widget's current palette came from, so its Quick Start
  // dropdown reflects it (image / theme / scheme), exactly like the tool pages.
  get paletteGenerator(): string {
    if ( this.paletteSubtitle?.kind === 'image' ) { return 'image'; }
    if ( this.paletteSource.theme ) { return 'theme'; }
    return this.paletteSource.scheme || '';
  }

  get gradientGenerator(): string {
    if ( this.gradientSubtitle?.kind === 'image' ) { return 'image'; }
    if ( this.gradientSource.theme ) { return 'theme'; }
    return this.gradientSource.scheme || '';
  }

  get wallpaperGenerator(): string {
    if ( this.wallpaperSubtitle?.kind === 'image' ) { return 'image'; }
    if ( this.wallpaperSource.theme ) { return 'theme'; }
    return this.wallpaperSource.scheme || '';
  }
  SERVICES: ProductLine[] = SERVICES;
  THEMES: ProductLine[] = THEMES;
  TOOLS: ProductLine[] = TOOLS;

  // Every converter tool from the /tools page, as the /tools page's compact cards, drifting down
  // the Web Tools box's Converters tab (owner 2026-10-11: all of them, animated).
  CONVERTER_TOOLS = ( TOOLS[ 0 ]?.products ?? [] ).filter( p => p.key.includes( '-to-' ) );
  /** a converter's line as on the tools page ("HEX (six digit web code) to HSL (...)", owner 2026-10-12) */
  readonly toolLine = toolLine;

  // The product showcases under the hero (product-showcase.component.ts): Dock Town, LangMi, then
  // Tidy Desktop (owner 2026-10-10).
  readonly dockTownShowcase = DOCK_TOWN_SHOWCASE;
  readonly langmiShowcase = LANGMI_SHOWCASE;
  readonly tidyDesktopShowcase = TIDY_DESKTOP_SHOWCASE;
  readonly aiTokenMeterShowcase = AI_TOKEN_METER_SHOWCASE;
  // The color picker of every platform in one card, under Tidy Desktop's (owner 2026-10-10).
  readonly colorPickersShowcase = COLOR_PICKERS_SHOWCASE;
  // Our Services as a box like the apps' (owner 2026-10-11), under the blog's.
  readonly servicesShowcase = SERVICES_SHOWCASE;


  darkModeActive = false;

  PLUGIN_SCREENSHOTS: any[] = [];

  // every reference, four across at the foot of the Our Services box, as the themes box's review bubbles
  // (owner 2026-10-11): the name with the role at its company under it, the company's name and logo in the
  // pill, or its written logo alone when it has one; no stars (references carry none)
  readonly REFERENCE_REVIEWS: Review[] = TESTIMONIALS.map( t => ( {
    reviewer: t.reviewer.name,
    date: t.reviewer.title,
    theme: ( t.reviewer.title.split( '@' )[ 1 ] || '' ).trim(),
    themeLogo: t.companyLogo,
    themeWordmark: t.companyWordmark,
    stars: 0,
    review: t.review,
    avatar: t.reviewer.photoUri,
  } ) );

  // Marketplace reviews (JetBrains + Open VSX) collected from the monitor stats
  // payload, shown as comment bubbles on the home page. Filled in getAllStats.
  HOME_REVIEWS: Review[] = [];

  // Until the monitor stats resolve, the reviews show silhouette placeholder cards.
  reviewsLoaded = false;

  // The home's blog box: every post, newest first (owner 2026-10-12: as the blog page's box), one at a time in the
  // box and all of them as icons in its All Posts row
  LATEST_POSTS: Blog[] = [ ...BLOG ].sort( ( a, b ) => ( a.date < b.date ? 1 : a.date > b.date ? -1 : 0 ) );

  environment = environment;
  environmentCommon = environmentCommon;

  heroLogo: string | undefined;


  pluginDownloadsLoaded = false;
  animatedDownloads = '0';
  downloadAnimationInterval?: any;
  actualDownloadsTotal = 0;

  selectedScreenshot: any;

  displayedPlatformScreenshot: any = null;
  private lastTargetScreenshotSrc = '';

  selectedPlatformIndex = 0;

  // Cached field versions of the hot getters/methods so the template doesn't
  // re-invoke them on every change-detection tick. Safari's JS engine pays
  // a real cost for repeated getter calls; binding to fields is essentially
  // free. Recomputed only when the underlying selection changes.
  visiblePlatforms: FlatPlatform[] = [];
  selectedPlatformCached: FlatPlatform | null = null;
  /** every label the CTA can wear: stacked in the button so it keeps the widest one's width (owner, 2026-09-20) */
  get ctaLabels(): readonly string[] { return platformCtaLabels( this.selectedScreenshot?.product ); }
  isSmallScreenCached = false;

  /** CTA label for the selected platform (per-platform monetization). */
  get ctaText(): string {
    const product = this.selectedScreenshot?.product;
    const name = this.selectedPlatformCached?.name;
    return platformCtaLabel( product, name, this.storeService.availability( platformSku( product, name ) ) );
  }

  /** a store listing the store has said it cannot sell: the CTA is inert (Coming soon); it stays active while the store is still answering */
  get selectedStoreUnavailable(): boolean {
    const sku = platformSku( this.selectedScreenshot?.product, this.selectedPlatformCached?.name );
    return !!sku && !this.storeService.canAdd( sku );
  }

  /** "$1.99" when the selected listing is sold on the site (live catalogue price, else the list price), else null. */
  get selectedPriceLabel(): string | null {
    const product = this.selectedScreenshot?.product;
    const name = this.selectedPlatformCached?.parentName || this.selectedPlatformCached?.name;
    const sku = platformSku( product, name );
    return sku ? this.storeService.priceLabel( sku, platformListPrice( product, name ) ) : freeStoreOfferLabel( product, name );
  }

  /** Whether the selected platform is only planned (CTA inactive, "Coming soon"). */
  get selectedPlatformPlanned(): boolean {
    return isPlatformPlanned( this.selectedScreenshot?.product, this.selectedPlatformCached?.name ) || this.selectedStoreUnavailable;
  }

  /** Total number of themes across every theme product line. */
  get totalThemesCount(): number {
    return this.THEMES.reduce( ( acc, line ) => acc + ( line.products?.length ?? 0 ), 0 );
  }

  /** Distinct platforms across all themes (flattening supportedPlatforms). */
  get uniquePlatformCount(): number {
    const names = new Set<string>();
    const collect = ( info: any ) => {
      if ( info?.supportedPlatforms?.length ) {
        info.supportedPlatforms.forEach( collect );
      } else if ( info?.name ) {
        names.add( info.name );
      }
    };
    for ( const line of this.THEMES ) {
      for ( const p of ( line.products ?? [] ) ) {
        for ( const plat of ( p.platforms ?? [] ) ) {
          collect( plat.info );
        }
      }
    }
    return names.size;
  }

  private smallScreenResizeListener?: () => void;

  activeThemeIndex = 0;

  // the middle of the (shuffled) theme list, where the newest theme is pinned: the themes box's start
  // when no premium theme is there to start on
  centerIndex = 0;

  // Auto-cycle the active theme's platform screenshots, like the theme page.
  private platformRotationInterval?: ReturnType<typeof setInterval>;
  private readonly PLATFORM_ROTATION_MS = 8000;

  // Home Color Palettes section: a small featured set rendered as the same
  // poster cards as the themes wall; clicking opens the palette page.

  constructor(
    private themeService: ThemeService,
    private routingService: RoutingService,
    private monitorProviderService: MonitorProviderService,
    public utilService: UtilService,
    private ngZone: NgZone,
    private colorService: ColorService,
    private paletteGen: PaletteGenerationService,
    private analyticsService: AnalyticsService,
    private wallpaperService: WallpaperService,
    private dynamicService: DynamicWallpaperService,
    private storeService: StoreService,
    private cartPopupService: CartPopupService,
    private directDownloadService: DirectDownloadService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.pluginDownloadsLoaded = false;

    this.modeSub = merge(
      this.themeService.darkLightSetting.pipe( tap( setting => this.darkLightSetting = setting ) ),
      this.themeService.productSurface.pipe( tap( surface => this.surfaceAppearance = surface?.appearance || null ) ),
    ).subscribe( () => this.applyHeroMode() );
    window.matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', this.systemSchemeListener );
  }

  private darkLightSetting: string | null = null;
  private surfaceAppearance: string | null = null;
  private modeSub: Subscription | undefined;
  private systemSchemeListener = () => {
    if ( this.surfaceAppearance === 'Dark' || this.surfaceAppearance === 'Light'
        || this.darkLightSetting === DarkLightSettings.dark || this.darkLightSetting === DarkLightSettings.light ) {
      return;
    }
    this.applyHeroMode();
    this.initGlobe();
    this.circuit?.pulse( 3 );
  };

  private applyHeroMode(): void {
    const dark = this.surfaceAppearance === 'Dark' ? true
        : this.surfaceAppearance === 'Light' ? false
        : this.getSrcWithDarkLightSetting( this.darkLightSetting || DarkLightSettings.auto, false, true );
    this.heroLogo = dark ? BRAND.logo.inlineDark : BRAND.logo.inlineLight;
    this.darkModeActive = dark;
  }

  ngAfterContentInit(): void {
    this.initGlobe();

    this.THEMES = [...THEMES];

    const allThemes: any[] = [];
    this.THEMES.forEach( productLine => {
      productLine.products.forEach( product => {
        ( product as any ).loaded = true;
        product.category = productLine.name;
        allThemes.push( {
          product,
          category: product.category,
          loaded: false,
        } );
      } );
    } );

    const shuffled = this.utilService.shuffle( allThemes );
    this.centerIndex = Math.floor( shuffled.length / 2 );
    // Pin the NEWEST-released theme (by releaseDate) to the CENTRE so the LCP
    // hero is deterministic (the server preloads it); the rest stay shuffled so
    // the wall looks fresh on every visit.
    const newestIdx = shuffled.reduce(
      ( best, t, i ) =>
        ( ( t.product?.releaseDate || '' ) > ( shuffled[ best ].product?.releaseDate || '' ) ? i : best ),
      0
    );
    if ( newestIdx !== this.centerIndex ) {
      [ shuffled[ newestIdx ], shuffled[ this.centerIndex ] ] =
        [ shuffled[ this.centerIndex ], shuffled[ newestIdx ] ];
    }
    this.PLUGIN_SCREENSHOTS = shuffled;
    // the box's rotation: the newest and the five newest after it, until the downloads come
    this.refreshThemeBoxRotation();

    // The themes box opens on a random premium theme, on a random one of its platforms (owner
    // 2026-10-10), a different one on every visit; the newest theme when there is no premium one.
    const premium = this.PLUGIN_SCREENSHOTS
      .map( ( t, i ) => ( { t, i } ) )
      .filter( ( { t } ) => t.product && t.product.key !== 'all-in-one' && !isThemeFree( t.product ) );
    const start = premium.length ? premium[ Math.floor( Math.random() * premium.length ) ].i : this.centerIndex;
    this.applyActiveBanner( start );
    if ( this.visiblePlatforms.length > 1 ) {
      this.selectedPlatformIndex = Math.floor( Math.random() * this.visiblePlatforms.length );
      this.selectedPlatformCached = this.visiblePlatforms[ this.selectedPlatformIndex ] ?? null;
    }

    // Small defer so the hero paints first, then fetch stats + reviews promptly.
    setTimeout(() => {
      // Marketplace stats + reviews, and the approved reviews of buyers on codigrate.com (owner,
      // 2026-10-04: they join the wall like the marketplace ones); the store call never fails.
      forkJoin( [
        this.monitorProviderService.getAllStats().pipe( catchError( () => of( {} as { [key: string]: any[] } ) ) ),
        this.storeService.reviews(),
      ] )
        .subscribe( ( [ statsByKey, storeReviews ] ) => {
          let totalDownloads = 0;
          const collectedReviews: Review[] = [];
          const productsByKey: { [ key: string ]: any } = {};
          this.THEMES.forEach( productLine => {
            productLine.products.forEach( product => {
              productsByKey[ product.key ] = product;
              const res = statsByKey[ product.key ];
              let total = 0;
              if ( res && Array.isArray( res ) ) {
                res.forEach( platformData => {
                  // Chrome reports installs as downloads once the monitor has them; its weekly
                  // "users" are the same people, so they only stand in while downloads are 0.
                  total += platformData.downloads || platformData.users || 0;
                } );
              }
              // Marketplace reviews ride the stats payload as comments[] (review-cards.ts).
              collectedReviews.push( ...marketplaceReviewCards( product, res ) );
              // one rating over every marketplace and the site's own buyers (-1 = loaded, none yet)
              if ( res && Array.isArray( res ) ) {
                product.rating = combinedRating( res, storeReviews?.products?.[ product.key ] ) || -1;
              }
              if ( product.rating === undefined ) {
                product.rating = 0;
              }

              this.PLUGIN_SCREENSHOTS.forEach( screenshot => {
                if ( screenshot.product.key === product.key ) {
                  screenshot.product.rating = product.rating;
                }
              } );
              if ( this.selectedScreenshot?.product?.key === product.key ) {
                this.selectedScreenshot.product.rating = product.rating;
              }

              // Always assign downloads (even on a failed/empty response) so
              // the per-theme counters stop showing the loading spinner.
              product.downloads = this.utilService.numberFormatter( total );
              product.totalDownloads = total;
              totalDownloads += total;
            } );
          } );
          this.actualDownloadsTotal = totalDownloads;
          this.pluginDownloadsLoaded = true;
          // the box's rotation: the most downloaded, now that the numbers are in
          this.refreshThemeBoxRotation();

          // the store's approved reviews with a comment, in the same bubbles
          Object.values( productsByKey ).forEach( product => collectedReviews.push( ...storeReviewCards( product, storeReviews?.reviews ) ) );

          // Every review goes to the reviews at the foot of the themes box; only
          // replace on a payload that actually carried reviews, so a failed or
          // empty refresh never blanks the reviews already shown.
          if ( collectedReviews.length ) {
            this.HOME_REVIEWS = this.utilService.shuffle( [ ...collectedReviews ] );
          }
          // Stats resolved (with or without reviews): drop the loading skeletons.
          this.reviewsLoaded = true;
        } );
    }, 250);
  }

  // ---- Web Tools box (owner 2026-10-11): one tool at a time, the chips switch it ----

  /** the tool the box shows (the AI Cost Calculator first, owner 2026-10-19) */
  webTool = 'ai-cost';
  /** the colour the colour tool shows, so its button opens the full tool on it */
  webToolColor = '';

  private toolOf( key: string ): any {
    return ( TOOLS[ 0 ]?.products ?? [] ).find( p => p.key === key ) || {};
  }

  /** the box's tools: the left side's copy, the headline and the full tool's route, which carries the
   *  colours the tool shows (as the tools' own buttons did) */
  get webToolSlides(): ToolSlide[] {
    const color = this.toolOf( 'color-card' );
    const palette = this.toolOf( 'palette-generator' );
    const gradient = this.toolOf( 'gradient-generator' );
    const wallpaper = this.toolOf( 'wallpaper-generator' );
    const dynamic = this.toolOf( 'dynamic-wallpaper-generator' );
    const code = this.toolOf( 'code-image-generator' );
    const contrast = this.toolOf( 'contrast-checker' );
    const cvd = this.toolOf( 'color-blindness-simulator' );
    const converters = this.CONVERTER_TOOLS;
    // the /tools page's categories and their icons, over each tool's name (owner 2026-10-11)
    const icons = './assets/product-lines/tools/';
    const colorTools = { eyebrow: 'Color Tools', eyebrowIcon: icons + 'color-tools.svg' };
    const generators = { eyebrow: 'Generators', eyebrowIcon: icons + 'web-tool.svg' };
    const converterCategory = { eyebrow: 'Converters', eyebrowIcon: icons + 'converters.svg' };
    const aiCost = AI_TOOLS[ 0 ];
    const aiWatch = AI_TOOLS[ 1 ];
    return [
      {
        // the first tool of the box (owner 2026-10-19), as on /tools; its button opens the full tool on the numbers here
        key: 'ai-cost', eyebrow: 'AI Tools', eyebrowIcon: icons + 'ai-cost-card.svg', chip: 'AI Cost', chipLogo: aiCost.logo,
        icon: aiCost.logo, title: aiCost.name, lead: aiCost.shortDescription,
        text: 'Pick what you are building, a chatbot, a coding assistant or document summaries, and see what it costs a month on every Claude, GPT, Gemini, Grok, Mistral and DeepSeek model.',
        facts: [ 'Free', 'Every Model and Platform', 'Prices Updated Daily' ],
        cta: 'Open AI Cost Calculator', link: URLS.tools.aiCostCalculator,
        queryParams: aiCostQueryParams( this.aiCost.values, this.aiCost.scenarioKey, this.aiCost.modelId ),
        accent: 'What Will Your AI App', rest: 'Cost a Month?',
        line: 'Every model priced at your numbers, the cheapest first',
      },
      {
        // the second AI tool (owner 2026-10-28), right after the calculator; its button opens the full board on the
        // model, price and range picked here
        key: 'ai-watch', eyebrow: 'AI Tools', eyebrowIcon: icons + 'ai-cost-card.svg', chip: 'AI Price Watch', chipLogo: aiWatch.logo,
        icon: aiWatch.logo, title: aiWatch.name, lead: aiWatch.shortDescription,
        text: 'Pick any Claude, GPT, Gemini, Grok, Mistral or DeepSeek model and see its token price on a daily chart, green when it rose and red when it fell.',
        facts: [ 'Free', 'Every Model and Platform', 'Prices Updated Daily' ],
        cta: 'Open AI Price Watch', link: URLS.tools.aiPriceWatch,
        queryParams: {
          model: this.aiWatch.model || null,
          field: this.aiWatch.field === AI_WATCH_DEFAULT_FIELD ? null : this.aiWatch.field,
          range: this.aiWatch.range === AI_WATCH_DEFAULT_RANGE ? null : this.aiWatch.range,
        },
        accent: 'Watch AI Token Prices', rest: 'Like a Stock',
        line: 'Every model\'s price per million tokens, day by day',
      },
      {
        key: 'color', ...colorTools, chip: 'Color Explorer', chipLogo: color.logo, icon: color.logo, title: color.name,
        lead: color.shortDescription, text: color.description,
        facts: [ 'Free', 'Every Color Space', 'Color Blindness Preview' ],
        cta: 'Open Color Explorer',
        link: this.webToolColor ? URLS.tools.color + '/' + this.webToolColor.replace( '#', '' ).toLowerCase() : URLS.tools.color,
        accent: 'Explore Any Color,', rest: 'Every Shade and Scheme',
        line: 'Tones, shades, tints and matching schemes, with a color blindness preview',
      },
      {
        key: 'contrast', ...colorTools, chip: 'Contrast Checker', chipLogo: contrast.logo, icon: contrast.logo, title: contrast.name,
        lead: contrast.shortDescription,
        text: 'Read the WCAG contrast ratio of any text on any background, with the AA and AAA checks, APCA, and the closest colors that pass when a pair fails.',
        facts: [ 'Free', 'WCAG AA and AAA', 'Closest Passing Colors' ],
        cta: 'Open Contrast Checker', link: URLS.tools.contrastChecker,
        queryParams: { fg: this.contrastFg.substring( 1 ).toLowerCase(), bg: this.contrastBg.substring( 1 ).toLowerCase() },
        accent: 'Check Your Contrast,', rest: 'Fix It in a Click',
        line: 'Normal text, large text and buttons against WCAG AA and AAA',
      },
      {
        key: 'cvd', ...colorTools, chip: 'Color Blindness', chipLogo: cvd.logo, icon: cvd.logo, title: cvd.name,
        lead: cvd.shortDescription,
        text: 'Upload, drop or paste any picture and drag the handle to compare it with eight color vision deficiencies, from deuteranomaly to achromatopsia, then download the result.',
        facts: [ 'Free', '8 Conditions', 'Nothing Uploaded' ],
        cta: 'Open Color Blindness Simulator', link: URLS.tools.colorBlindnessSimulator,
        accent: 'See Your Design', rest: 'Through Other Eyes',
        line: 'Drag the handle to compare the original with each color vision deficiency',
      },
      {
        key: 'palette', ...generators, chip: 'Palette', chipLogo: palette.logo, icon: palette.logo, title: palette.name,
        lead: palette.shortDescription, text: palette.description,
        facts: [ 'Free', 'Up To 10 Colors', 'From a Photo' ],
        cta: 'Open Palette Generator', link: this.paletteRoute, queryParams: this.paletteQueryParams,
        accent: 'Build a Palette', rest: 'in Seconds',
        line: 'Start from a mood or a photo, then reorder, edit and copy every color',
      },
      {
        key: 'gradient', ...generators, chip: 'Gradient', chipLogo: gradient.logo, icon: gradient.logo, title: gradient.name,
        lead: gradient.shortDescription, text: gradient.description,
        facts: [ 'Free', 'Linear, Radial and Conic', 'Copy the CSS' ],
        cta: 'Open Gradient Generator', link: this.gradientRoute, queryParams: this.gradientQueryParams,
        accent: 'Make a Gradient,', rest: 'Copy the CSS',
        line: 'Set the stops and the angle, then copy code ready for your stylesheet',
      },
      {
        key: 'wallpaper', ...generators, chip: 'Wallpaper', chipLogo: wallpaper.logo, icon: wallpaper.logo, title: wallpaper.name,
        lead: wallpaper.shortDescription,
        // the catalogue's description is too long for the box's fixed four lines
        text: 'Turn any palette into a mesh, wave, aurora, low poly, synthwave or one of many more styles, and download it sized for 4K, 5K, ultrawide, MacBook, iPhone, Android and iPad.',
        facts: [ 'Free', WALLPAPER_STYLES.length + ' Styles', '4K, 5K, iPhone and iPad' ],
        cta: 'Open Wallpaper Generator', link: this.wallpaperRoute, queryParams: this.wallpaperQueryParams,
        accent: 'Turn Any Palette', rest: 'Into a Wallpaper',
        line: 'Mesh, waves, aurora, low poly and more, sized for every screen',
      },
      {
        key: 'dynamic', ...generators, chip: 'Dynamic Wallpaper', chipLogo: dynamic.logo, icon: dynamic.logo, title: dynamic.name,
        lead: dynamic.shortDescription,
        text: 'The same wallpaper lit for every hour, from sunrise to night, in one .heic that macOS changes by the clock, by the sun or with Light and Dark mode.',
        facts: [ 'Free', 'HEIC for macOS', 'Time, Sun or Light & Dark' ],
        cta: 'Open Dynamic Wallpaper', link: this.dynamicRoute, queryParams: this.dynamicQueryParams,
        accent: 'One Wallpaper,', rest: 'Lit for Every Hour',
        line: 'Noon in your colors, a warm sunrise and sunset, a moonlit night',
      },
      {
        key: 'code', ...generators, chip: 'Code Snippet', chipLogo: code.logo, icon: code.logo, title: code.name,
        lead: code.shortDescription, text: code.description,
        facts: [ 'Free', CODE_IMAGE_THEMES.length + ' Themes', 'PNG up to 3x' ],
        cta: 'Open Code Snippet Generator', link: URLS.tools.codeImage, queryParams: this.codeSnippetQueryParams,
        accent: 'Turn Your Code', rest: 'Into a Picture',
        line: 'Your snippet in the editor colors of any Codigrate theme, ready to share',
      },
      {
        key: 'converters', ...converterCategory, chip: 'Converters', chipLogo: converters[ 0 ]?.logo, icon: converters[ 0 ]?.logo, title: 'Converters',
        lead: 'Convert any color between HEX and every other format',
        text: 'Paste a value and get the other format instantly, each with a live preview and the math worked out step by step.',
        facts: [ 'Free', converters.length + ' Converters', 'Both Ways' ],
        cta: 'All Tools', link: URLS.tools.tools,
        accent: 'Convert Colors', rest: 'Between Every Format',
        line: 'RGB, HSL, CMYK, OKLCH, CIELAB and more, both ways, with a live preview',
      },
    ];
  }

  // ---- AI Cost Calculator (2026-10-19): the tool's own card; the full tool opens on the numbers picked here ----

  aiCost: { values: AiCostValues; scenarioKey: string; modelId: string | null } =
    { values: { ...AI_COST_DEFAULTS }, scenarioKey: 'chatbot', modelId: null };

  onAiCostCard( change: AiCostCardChange ): void {
    this.aiCost = { values: change.values, scenarioKey: change.scenarioKey, modelId: change.modelId };
  }

  // ---- AI Price Watch (2026-10-28): the board itself; the full tool opens on the model, price and range picked here ----

  aiWatch: { model: string; field: AiWatchField; range: AiWatchRange } =
    { model: '', field: AI_WATCH_DEFAULT_FIELD, range: AI_WATCH_DEFAULT_RANGE };

  onAiWatchBoard( change: AiWatchBoardChange ): void {
    this.aiWatch = { model: change.model, field: change.field, range: change.range };
  }

  // ---- Colour blindness simulator (2026-10-15): the card opens on a random theme's IDE screenshot ----

  readonly cvdStart = randomThemeScreenshot( ( [] as any[] ).concat( ...THEMES.map( line => line.products ) ) )?.src || '';

  // ---- Contrast checker (2026-10-15): the tool's own card; the full tool opens on the pair picked here ----

  // a random Codigrate theme's editor text on its editor background, a new one each visit (owner 2026-10-15)
  private readonly contrastStart = randomThemeContrastPair();
  contrastFg = this.contrastStart.fg;
  contrastBg = this.contrastStart.bg;

  onContrastColors( pair: { fg: string; bg: string } ): void {
    this.contrastFg = pair.fg;
    this.contrastBg = pair.bg;
  }

  // ---- Code snippet (2026-10-13): the tool's own card; the full tool opens on its theme and language ----

  codeSnippetState: CodeImageState | null = null;

  get codeSnippetQueryParams(): { [ key: string ]: string } | null {
    const state = this.codeSnippetState;
    return state ? { theme: state.theme.key, lang: state.language.key } : null;
  }

  openCodeSnippet(): void {
    this.router.navigate( [ URLS.tools.codeImage ], { queryParams: this.codeSnippetQueryParams || {} } );
  }

  pickWebTool( key: string ): void {
    this.webTool = key;
  }

  /** the full tool's button in the box (the wallpaper tool logs it, as its own button did) */
  onWebToolCta( key: string ): void {
    if ( key === 'wallpaper' ) {
      this.onWallpaperDetails();
    }
    if ( key === 'dynamic' ) {
      this.analyticsService.logEvent( 'Home Dynamic Wallpaper CTA Clicked', { style: this.wallpaperStyle, mode: this.dynamicMode } );
    }
    if ( key === 'code' ) {
      this.analyticsService.logEvent( 'Home Code Snippet CTA Clicked', this.codeSnippetQueryParams || {} );
    }
  }

  // ---- Palette tool widget (local, non-navigating teaser) ----

  // How the current widget palette was made, so the CTA can carry it to the full
  // tool (and the full tool shows the same "Generated from ..." subtitle).
  paletteSource: { theme?: string; scheme?: string; initializer?: string; seed?: string } = {};

  // Route to the full palette tool for the current colours.
  get paletteRoute(): string {
    const hexes = this.paletteColors.map( c => c.value.replace( '#', '' ) ).join( '-' );
    return hexes ? URLS.tools.palette + '/' + hexes : URLS.tools.palette;
  }

  // Query params that reproduce the subtitle on the full palette tool.
  get paletteQueryParams(): { [ key: string ]: string } | null {
    if ( this.paletteSource.theme ) {
      return { theme: this.paletteSource.theme };
    }
    if ( this.paletteSource.scheme ) {
      const q: { [ key: string ]: string } = { scheme: this.paletteSource.scheme };
      if ( this.paletteSource.initializer ) { q.initializer = this.paletteSource.initializer; }
      if ( this.paletteSource.seed ) { q.seed = this.paletteSource.seed; }
      return q;
    }
    return null;
  }

  private seedPalette(): void {
    const start = this.paletteGen.randomStart( PALETTE_SCHEME_MINS );
    if ( !start.hexes.length ) {
      return;
    }
    this.paletteCount = start.hexes.length;
    this.paletteColors = start.hexes.map( h => ( { value: h } ) );
    if ( start.theme ) {
      const name = ( start.theme.name || '' ).trim().replace( /\s*theme\s*$/i, '' ) + ' Theme';
      this.paletteSubtitle = { kind: 'theme', themeName: name, logo: start.theme.logo };
      this.paletteSource = { theme: start.theme.key };
    } else {
      this.paletteSubtitle = this.subtitleFor( start );
      this.paletteSource = { scheme: start.scheme, initializer: start.initializer, seed: start.seed };
    }
  }

  private subtitleFor( result: { scheme?: string; initializer?: string; hexes: string[]; seed?: string } ): PaletteSubtitle {
    const sourced = [ 'complementary', 'split complementary', 'triadic', 'tetradic', 'analogous', 'monochrome', 'warm', 'cool' ];
    if ( result.scheme && sourced.includes( result.scheme ) && result.initializer ) {
      const name = result.initializer.replace( /-/g, ' ' ).replace( /\b\w/g, c => c.toUpperCase() );
      const color = HEX_CODES[ result.initializer ] ? '#' + HEX_CODES[ result.initializer ] : ( result.hexes[ 0 ] || '' );
      return { kind: 'harmony', scheme: result.scheme, name, color };
    }
    if ( result.scheme === 'full' ) {
      return { kind: 'full', seed: result.seed || '' };
    }
    return { kind: '' };
  }

  onPaletteGenerate( scheme: string ): void {
    if ( scheme === 'theme' || scheme.startsWith( 'theme:' ) ) {
      // 'theme:<key>' = the theme picked in the generator (owner 2026-10-12), 'theme' = any theme
      const t = scheme.startsWith( 'theme:' ) ? this.paletteGen.themeByKey( scheme.slice( 6 ) ) : this.paletteGen.randomTheme( this.paletteSource.theme );
      if ( !t.hexes.length || !t.theme ) { return; }
      this.paletteColors = t.hexes.map( h => ( { value: h } ) );
      const name = ( t.theme.name || '' ).trim().replace( /\s*theme\s*$/i, '' ) + ' Theme';
      this.paletteSubtitle = { kind: 'theme', themeName: name, logo: t.theme.logo };
      this.paletteSource = { theme: t.theme.key };
      return;
    }
    const result = this.paletteGen.build( scheme, this.paletteCount );
    if ( !result.hexes.length ) {
      return;
    }
    this.paletteColors = result.hexes.map( h => ( { value: h } ) );
    this.paletteSubtitle = this.subtitleFor( result );
    this.paletteSource = { scheme: result.scheme, initializer: result.initializer, seed: result.seed };
  }

  // Manual edits make the palette custom: swap the "generated from" attribution
  // for a Custom Theme line with a stable seed fingerprint.
  private markCustom(): void {
    this.paletteSubtitle = { kind: 'custom', seed: this.paletteGen.paletteSeed( this.paletteColors.map( c => c.value ) ) };
    this.paletteSource = {};
  }

  private markGradientCustom(): void {
    this.gradientSubtitle = { kind: 'custom', seed: this.paletteGen.paletteSeed( this.gradientColors.map( c => c.value ) ) };
    this.gradientSource = {};
  }

  onPaletteEdit( change: { index: number; value: string } ): void {
    if ( this.paletteColors[ change.index ] ) {
      this.paletteColors[ change.index ].value = change.value;
      this.markCustom();
    }
  }

  onPaletteReorder( event: CdkDragDrop<unknown> ): void {
    moveItemInArray( this.paletteColors, event.previousIndex, event.currentIndex );
  }

  onPaletteAdd(): void {
    if ( this.paletteColors.length < 10 ) {
      this.paletteColors.push( { value: this.colorService.generateRandomHexColor() } );
      this.markCustom();
    }
  }

  onPaletteReverse(): void {
    this.paletteColors = [ ...this.paletteColors ].reverse();
    this.markCustom();
  }

  onPaletteRemove( index: number ): void {
    if ( this.paletteColors.length > 3 ) {
      this.paletteColors.splice( index, 1 );
      this.markCustom();
    }
  }

  onPaletteUpload( event: Event ): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[ 0 ];
    if ( !file ) {
      return;
    }
    this.paletteGen.buildFromImage( file ).then( img => {
      this.ngZone.run( () => {
        this.paletteColors = img.hexes.map( h => ( { value: h } ) );
        this.paletteSubtitle = { kind: 'image', imageSrc: img.src, imageName: img.id };
        this.paletteSource = {};
      } );
    } ).catch( () => { /* ignore unreadable image */ } );
  }

  // ----- Gradient widget (mirrors the palette widget; preview only, no nav) -----

  // A random gradient type + angle, so the home preview varies linear / radial /
  // conic and its lean on every seed and Generate, exactly like the gradient tool.
  private randomGradientType(): 'linear' | 'radial' | 'conic' {
    const types: ( 'linear' | 'radial' | 'conic' )[] = [ 'linear', 'radial', 'conic' ];
    return types[ Math.floor( Math.random() * types.length ) ];
  }

  private randomGradientAngle(): number {
    return Math.floor( Math.random() * 360 );
  }

  private seedGradient(): void {
    const start = this.paletteGen.randomStart( GRADIENT_SCHEME_MINS );
    if ( !start.hexes.length ) {
      return;
    }
    this.gradientCount = start.hexes.length;
    this.gradientColors = start.hexes.map( h => ( { value: h } ) );
    // A fresh seed also gets a random type + angle (the gradient tool does this).
    this.gradientType = this.randomGradientType();
    this.gradientAngle = this.randomGradientAngle();
    if ( start.theme ) {
      const name = ( start.theme.name || '' ).trim().replace( /\s*theme\s*$/i, '' ) + ' Theme';
      this.gradientSubtitle = { kind: 'theme', themeName: name, logo: start.theme.logo };
      this.gradientSource = { theme: start.theme.key };
    } else {
      this.gradientSubtitle = this.subtitleFor( start );
      this.gradientSource = { scheme: start.scheme, initializer: start.initializer, seed: start.seed };
    }
  }

  get gradientRoute(): string {
    const hexes = this.gradientColors.map( c => c.value.replace( '#', '' ) ).join( '-' );
    return hexes ? URLS.tools.gradient + '/' + hexes : URLS.tools.gradient;
  }

  get gradientQueryParams(): { [ key: string ]: string } {
    const q: { [ key: string ]: string } = { type: this.gradientType };
    if ( this.gradientType !== 'radial' ) { q.angle = String( this.gradientAngle ); }
    if ( this.gradientSource.theme ) {
      q.theme = this.gradientSource.theme;
    } else if ( this.gradientSource.scheme ) {
      q.scheme = this.gradientSource.scheme;
      if ( this.gradientSource.initializer ) { q.initializer = this.gradientSource.initializer; }
      if ( this.gradientSource.seed ) { q.seed = this.gradientSource.seed; }
    }
    return q;
  }

  onGradientGenerate( scheme: string ): void {
    this.gradientPositions = [];
    // Every Generate also re-rolls the gradient type + angle (matches the tool).
    this.gradientType = this.randomGradientType();
    this.gradientAngle = this.randomGradientAngle();
    if ( scheme === 'theme' || scheme.startsWith( 'theme:' ) ) {
      // 'theme:<key>' = the theme picked in the generator (owner 2026-10-12), 'theme' = any theme
      const t = scheme.startsWith( 'theme:' ) ? this.paletteGen.themeByKey( scheme.slice( 6 ) ) : this.paletteGen.randomTheme( this.gradientSource.theme );
      if ( !t.hexes.length || !t.theme ) { return; }
      this.gradientColors = t.hexes.slice( 0, this.gradientCount ).map( h => ( { value: h } ) );
      const name = ( t.theme.name || '' ).trim().replace( /\s*theme\s*$/i, '' ) + ' Theme';
      this.gradientSubtitle = { kind: 'theme', themeName: name, logo: t.theme.logo };
      this.gradientSource = { theme: t.theme.key };
      return;
    }
    if ( scheme === 'featured' ) {
      // A curated two-stop named gradient (same as the gradient tool's Featured
      // generator). build() has no 'featured' case, so this must be handled here
      // or it would fall through to a random palette and mis-bind the dropdown.
      const f = this.paletteGen.featuredGradient();
      if ( !f.hexes.length ) { return; }
      this.gradientColors = f.hexes.map( h => ( { value: h } ) );
      this.gradientSubtitle = { kind: 'named', name: f.name, gradient: `linear-gradient(90deg, ${ f.hexes.join( ', ' ) })` };
      this.gradientSource = { scheme: 'featured' };
      return;
    }
    const result = this.paletteGen.build( scheme, this.gradientCount );
    if ( !result.hexes.length ) {
      return;
    }
    this.gradientColors = result.hexes.map( h => ( { value: h } ) );
    this.gradientSubtitle = this.subtitleFor( result );
    this.gradientSource = { scheme: result.scheme, initializer: result.initializer, seed: result.seed };
  }

  onGradientEdit( change: { index: number; value: string } ): void {
    if ( this.gradientColors[ change.index ] ) {
      this.gradientColors[ change.index ].value = change.value;
      this.markGradientCustom();
    }
  }

  onGradientReverse(): void {
    this.gradientColors = [ ...this.gradientColors ].reverse();
    if ( this.gradientPositions.length === this.gradientColors.length ) {
      this.gradientPositions = this.gradientPositions.map( p => +( 100 - p ).toFixed( 2 ) ).reverse();
    }
    this.markGradientCustom();
  }

  onGradientRemove( index: number ): void {
    if ( this.gradientColors.length > 2 ) {
      this.gradientColors.splice( index, 1 );
      if ( this.gradientPositions.length > index ) {
        this.gradientPositions.splice( index, 1 );
      }
      this.markGradientCustom();
    }
  }

  private ensureGradientPositions(): void {
    const n = this.gradientColors.length;
    if ( this.gradientPositions.length !== n ) {
      this.gradientPositions = this.gradientColors.map( ( _, k ) => ( n > 1 ? +( k / ( n - 1 ) * 100 ).toFixed( 2 ) : 0 ) );
    }
  }

  onGradientPosition( change: { index: number; value: number } ): void {
    this.ensureGradientPositions();
    const i = change.index;
    const lo = i > 0 ? this.gradientPositions[ i - 1 ] : 0;
    const hi = i < this.gradientPositions.length - 1 ? this.gradientPositions[ i + 1 ] : 100;
    this.gradientPositions[ i ] = Math.max( lo, Math.min( hi, change.value ) );
  }

  onGradientInsertStop( at: { position: number } ): void {
    if ( this.gradientColors.length >= 10 ) { return; }
    this.ensureGradientPositions();
    const pos = Math.max( 0, Math.min( 100, at.position ) );
    let idx = this.gradientPositions.findIndex( p => p > pos );
    if ( idx === -1 ) { idx = this.gradientPositions.length; }
    const leftHex = this.gradientColors[ idx - 1 ]?.value ?? this.gradientColors[ 0 ]?.value ?? '#000000';
    const rightHex = this.gradientColors[ idx ]?.value ?? leftHex;
    const leftPos = idx > 0 ? this.gradientPositions[ idx - 1 ] : 0;
    const rightPos = idx < this.gradientPositions.length ? this.gradientPositions[ idx ] : 100;
    const t = rightPos > leftPos ? ( pos - leftPos ) / ( rightPos - leftPos ) : 0.5;
    const value = this.colorService.interpolateColors( leftHex, rightHex, t ).toLowerCase();
    this.gradientColors.splice( idx, 0, { value } );
    this.gradientPositions.splice( idx, 0, pos );
    this.markGradientCustom();
  }

  onGradientUpload( event: Event ): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[ 0 ];
    if ( !file ) {
      return;
    }
    this.paletteGen.buildFromImage( file ).then( img => {
      this.ngZone.run( () => {
        this.gradientColors = img.hexes.map( h => ( { value: h } ) );
        this.gradientPositions = [];
        this.gradientSubtitle = { kind: 'image', imageSrc: img.src, imageName: img.id };
        this.gradientSource = {};
      } );
    } ).catch( () => { /* ignore unreadable image */ } );
  }

  // ---- wallpaper widget (home teaser of /tools/wallpaper)

  private seedWallpaper(): void {
    // a random Codigrate theme palette (they make the best wallpapers), a random style and seed
    const t = this.paletteGen.randomTheme();
    if ( !t.hexes.length || !t.theme ) { return; }
    this.wallpaperColors = t.hexes.map( h => ( { value: h } ) );
    this.wallpaperCount = t.hexes.length;
    const name = ( t.theme.name || '' ).trim().replace( /\s*theme\s*$/i, '' ) + ' Theme';
    this.wallpaperSubtitle = { kind: 'theme', themeName: name, logo: t.theme.logo };
    this.wallpaperSource = { theme: t.theme.key };
    this.wallpaperStyle = WALLPAPER_STYLES[ Math.floor( Math.random() * WALLPAPER_STYLES.length ) ].key;
    this.wallpaperParams = defaultParams( this.wallpaperStyle );
    this.wallpaperSeed = this.wallpaperService.randomSeed();
  }

  get wallpaperRoute(): string {
    const hexes = this.wallpaperColors.map( c => c.value.replace( '#', '' ) ).join( '-' );
    return hexes ? URLS.tools.wallpaper + '/' + hexes : URLS.tools.wallpaper;
  }

  get wallpaperQueryParams(): { [ key: string ]: string } {
    const q: { [ key: string ]: string } = { style: this.wallpaperStyle, art: String( this.wallpaperSeed ) };
    if ( this.wallpaperSizeKey !== 'fhd' ) { q.size = this.wallpaperSizeKey; }
    if ( !paramsAreDefault( this.wallpaperStyle, this.wallpaperParams ) ) { q.p = this.wallpaperParams.join( '-' ); }
    if ( this.wallpaperSource.theme ) {
      q.theme = this.wallpaperSource.theme;
    } else if ( this.wallpaperSource.scheme ) {
      q.scheme = this.wallpaperSource.scheme;
      if ( this.wallpaperSource.initializer ) { q.initializer = this.wallpaperSource.initializer; }
      if ( this.wallpaperSource.seed ) { q.seed = this.wallpaperSource.seed; }
    }
    return q;
  }

  // ---- Dynamic wallpaper (2026-10-13): the wallpaper box's palette, style and seed, lit for every hour ----

  readonly DYNAMIC_MODES = DYNAMIC_MODES;
  readonly FRAME_COUNTS = FRAME_COUNTS;
  dynamicMode: DynamicMode = 'solar';
  dynamicFrames = 8;
  dynamicNight = DEFAULT_DAY_LIGHT.night;
  dynamicGlow = DEFAULT_DAY_LIGHT.glow;
  /** the whole day as one picture (day-bands.ts), the widget's preview */
  dynamicDayUrl = '';
  dynamicLoading = false;
  private dynamicSignature = '';
  private dynamicTimer: any = null;
  private dynamicSettingsCache: DynamicSettings | null = null;

  get dynamicSettings(): DynamicSettings {
    const s = this.dynamicSettingsCache;
    if ( !s || s.mode !== this.dynamicMode || s.frames !== this.dynamicFrames || s.night !== this.dynamicNight || s.glow !== this.dynamicGlow ) {
      this.dynamicSettingsCache = { mode: this.dynamicMode, frames: this.dynamicFrames, night: this.dynamicNight, glow: this.dynamicGlow };
    }
    return this.dynamicSettingsCache!;
  }

  onDynamicChange( change: Partial<DynamicSettings> ): void {
    if ( change.mode ) this.dynamicMode = DYNAMIC_MODES.find( m => m.key === change.mode )?.key || 'solar';
    if ( change.frames !== undefined && FRAME_COUNTS.indexOf( change.frames ) > -1 ) this.dynamicFrames = change.frames;
    if ( change.night !== undefined ) this.dynamicNight = Math.max( 0, Math.min( 100, change.night ) );
    if ( change.glow !== undefined ) this.dynamicGlow = Math.max( 0, Math.min( 100, change.glow ) );
  }

  get dynamicRoute(): string {
    const hexes = this.wallpaperColors.map( c => c.value.replace( '#', '' ) ).join( '-' );
    return hexes ? URLS.tools.dynamicWallpaper + '/' + hexes : URLS.tools.dynamicWallpaper;
  }

  get dynamicQueryParams(): { [ key: string ]: string } {
    const q: { [ key: string ]: string } = { ...this.wallpaperQueryParams };
    delete q.size;
    if ( this.dynamicMode !== 'solar' ) q.mode = this.dynamicMode;
    if ( this.dynamicFrames !== 8 ) q.frames = String( this.dynamicFrames );
    if ( this.dynamicNight !== DEFAULT_DAY_LIGHT.night ) q.night = String( this.dynamicNight );
    if ( this.dynamicGlow !== DEFAULT_DAY_LIGHT.glow ) q.glow = String( this.dynamicGlow );
    return q;
  }

  /** the day picture is drawn again when the box shows and something it is made of changed (ngDoCheck) */
  private checkDynamicDay(): void {
    if ( this.webTool !== 'dynamic' || !this.wallpaperColors.length ) return;
    const sig = [ this.wallpaperColors.map( c => c.value ).join( ',' ), this.wallpaperStyle, this.wallpaperSeed, this.wallpaperParams.join( '-' ),
      this.dynamicMode, this.dynamicFrames, this.dynamicNight, this.dynamicGlow ].join( '|' );
    if ( sig === this.dynamicSignature ) return;
    this.dynamicSignature = sig;
    this.dynamicLoading = true;
    if ( this.dynamicTimer ) clearTimeout( this.dynamicTimer );
    // a breath first, so the loader shows before the picture is drawn
    this.dynamicTimer = setTimeout( () => {
      this.dynamicTimer = null;
      this.dynamicDayUrl = this.drawDynamicDay();
      this.dynamicLoading = false;
    }, 60 );
  }

  /** the picture drawn once, each look of the day its band with that hour's light (the tool's own way) */
  private drawDynamicDay(): string {
    try {
      const options: DynamicWallpaperOptions = {
        style: this.wallpaperStyle, colors: this.wallpaperColors.map( c => c.value ), seed: this.wallpaperSeed, params: this.wallpaperParams,
        mode: this.dynamicMode, frames: this.dynamicFrames, light: { night: this.dynamicNight, glow: this.dynamicGlow },
      };
      const w = 960, h = 540;
      const canvas = document.createElement( 'canvas' );
      canvas.width = w;
      canvas.height = h;
      this.dynamicService.renderBase( canvas, options );
      const base = canvas.getContext( '2d' )!.getImageData( 0, 0, w, h ).data;
      const looks = distinctLooks( bandOrder( this.dynamicService.frames( options ) ) );
      const pixels = composeBands( w, h, looks.map( f => {
        const layer = new Uint8ClampedArray( base );
        this.dynamicService.relightPixels( layer, options, f );
        return layer;
      } ) );
      const out = document.createElement( 'canvas' );
      out.width = w;
      out.height = h;
      out.getContext( '2d' )!.putImageData( new ImageData( pixels, w, h ), 0, 0 );
      return out.toDataURL( 'image/jpeg', 0.88 );
    } catch {
      return '';
    }
  }

  private markWallpaperCustom(): void {
    this.wallpaperSubtitle = { kind: 'custom', seed: this.paletteGen.paletteSeed( this.wallpaperColors.map( c => c.value ) ) };
    this.wallpaperSource = {};
  }

  onWallpaperGenerate( scheme: string ): void {
    this.wallpaperSeed = this.wallpaperService.randomSeed();
    this.analyticsService.logEvent( 'Wallpaper Initialized', { source: scheme, page: 'home' } );
    if ( scheme === 'theme' || scheme.startsWith( 'theme:' ) ) {
      // 'theme:<key>' = the theme picked in the generator (owner 2026-10-12), 'theme' = another theme each time
      const t = scheme.startsWith( 'theme:' ) ? this.paletteGen.themeByKey( scheme.slice( 6 ) ) : this.paletteGen.randomTheme( this.wallpaperSource?.theme );
      if ( !t.hexes.length || !t.theme ) { return; }
      this.wallpaperColors = t.hexes.slice( 0, this.wallpaperCount ).map( h => ( { value: h } ) );
      const name = ( t.theme.name || '' ).trim().replace( /\s*theme\s*$/i, '' ) + ' Theme';
      this.wallpaperSubtitle = { kind: 'theme', themeName: name, logo: t.theme.logo };
      this.wallpaperSource = { theme: t.theme.key };
      return;
    }
    const result = this.paletteGen.build( scheme, this.wallpaperCount );
    if ( !result.hexes.length ) { return; }
    this.wallpaperColors = result.hexes.map( h => ( { value: h } ) );
    this.wallpaperSubtitle = this.subtitleFor( result );
    this.wallpaperSource = { scheme: result.scheme, initializer: result.initializer, seed: result.seed };
  }

  onWallpaperEdit( change: { index: number; value: string } ): void {
    if ( this.wallpaperColors[ change.index ] ) {
      this.wallpaperColors[ change.index ].value = change.value;
      this.markWallpaperCustom();
    }
  }

  onWallpaperReverse(): void {
    this.wallpaperColors = [ ...this.wallpaperColors ].reverse();
    this.markWallpaperCustom();
  }

  onWallpaperReorder( event: CdkDragDrop<unknown> ): void {
    moveItemInArray( this.wallpaperColors, event.previousIndex, event.currentIndex );
    this.wallpaperColors = [ ...this.wallpaperColors ];
    this.markWallpaperCustom();
  }

  onWallpaperAdd(): void {
    if ( this.wallpaperColors.length >= 10 ) { return; }
    this.wallpaperColors = [ ...this.wallpaperColors, { value: this.colorService.generateRandomHexColor() } ];
    this.markWallpaperCustom();
  }

  onWallpaperRemove( index: number ): void {
    if ( this.wallpaperColors.length > 2 ) {
      this.wallpaperColors.splice( index, 1 );
      this.wallpaperColors = [ ...this.wallpaperColors ];
      this.markWallpaperCustom();
    }
  }

  onWallpaperUpload( event: Event ): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[ 0 ];
    if ( !file ) {
      return;
    }
    this.paletteGen.buildFromImage( file ).then( img => {
      this.ngZone.run( () => {
        this.wallpaperColors = img.hexes.map( h => ( { value: h } ) );
        this.wallpaperSubtitle = { kind: 'image', imageSrc: img.src, imageName: img.id };
        this.wallpaperSource = {};
        this.wallpaperSeed = this.wallpaperService.randomSeed();
        this.analyticsService.logEvent( 'Wallpaper Initialized', { source: 'image', page: 'home' } );
      } );
    } ).catch( () => { /* ignore unreadable image */ } );
  }

  onWallpaperStyle( style: WallpaperStyle ): void {
    this.wallpaperStyle = style;
    this.wallpaperParams = defaultParams( style );
    this.analyticsService.logEvent( 'Wallpaper Style Changed', { style, page: 'home' } );
  }


  onWallpaperParams( params: number[] ): void {
    this.wallpaperParams = params;
    this.analyticsService.logEvent( 'Wallpaper Params Changed', { style: this.wallpaperStyle, page: 'home' } );
  }

  /** the "Open the Wallpaper Generator" button under the home widget */
  onWallpaperDetails(): void {
    this.analyticsService.logEvent( 'Wallpaper CTA Clicked', { style: this.wallpaperStyle, page: 'home' } );
  }

  ngOnInit(): void {
    this.motifSub = this.themeService.productTheme.subscribe( theme => {
      this.heroMotif = heroMotifFor( theme?.key );
      // the motif lights itself with the theme's colour circle, all five colours (owner, 2026-10-16)
      const product = THEMES.reduce( ( all, line ) => all.concat( line.products ), [] as Product[] )
        .find( p => p.key === theme?.key );
      this.heroPalette = product?.compactPalette || null;
      this.heroThemeTokens = product?.designTokens || null;
      this.heroThemeKey = product?.key || null;
      this.queueHeroCounterColor();
    } );
    // the theme's surfaces land a moment after the theme itself: the counter's colour is picked again on them
    this.surfaceSub = this.themeService.productSurface.subscribe( surface => {
      this.heroInputBackground = surface?.inputBackground || null;
      this.queueHeroCounterColor();
    } );
    // the appearance changed (mode, colour or design in the Appearance menu): the hero's board comes alive for
    // three seconds, eased in and out (owner 2026-10-12); each setting's first value is the page opening, not a change
    this.appearanceSub = merge(
      this.themeService.darkLightSetting.pipe( skip( 1 ) ),
      this.themeService.themeColorSetting.pipe( skip( 1 ) ),
      this.themeService.themeDesignSetting.pipe( skip( 1 ) ),
    ).pipe( debounceTime( 50 ) ).subscribe( () => {
      this.circuit?.pulse( 3 );
      this.queueHeroCounterColor();
    } );
    this.fragmentSub = this.route.fragment.subscribe( fragment => this.onServiceFragment( fragment ) );
    this.seedPalette();
    this.seedGradient();
    this.seedWallpaper();

    this.THEMES.forEach( productLine => {
      productLine.products.forEach( product => {
        product.rating = undefined;
      } );
    } );

    this.PLUGIN_SCREENSHOTS.forEach( screenshot => {
      screenshot.product.rating = undefined;
    } );

    // Cache isSmallScreen() result and refresh only on resize, so the
    // template's *ngIfs don't invoke the service method on every CD tick.
    this.isSmallScreenCached = this.utilService.isSmallScreen();
    this.smallScreenResizeListener = () => {
      this.isSmallScreenCached = this.utilService.isSmallScreen();
    };
    window.addEventListener( 'resize', this.smallScreenResizeListener, { passive: true } );

    // The themes box turns through a few themes by itself, and their platforms (2026-10-10).
    this.startThemeBoxRotation();
    this.startPlatformRotation();
    this.startDownloadAnimation();
  }

  private chargeBoard( on: boolean ): void {
    this.boardCharged = on;
    this.circuit?.charge( on );
  }

  private startDownloadAnimation() {
    // the board storms while the counter counts, and settles once the number stands; a cap in
    // case the stats never arrive (a local run without the server)
    this.chargeBoard( true );
    clearTimeout( this.circuitChargeCap );
    this.circuitChargeCap = setTimeout( () => this.chargeBoard( false ), 15000 );
    this.downloadAnimationInterval = this.utilService.startDownloadAnimation(
      () => this.pluginDownloadsLoaded,
      ( value ) => this.animatedDownloads = value,
      ( finalValue ) => {
        this.animatedDownloads = finalValue;
        this.downloadAnimationInterval = undefined;
        clearTimeout( this.circuitChargeCap );
        this.chargeBoard( false );
      },
      () => this.actualDownloadsTotal,
      HERO_COUNTER_DURATION_MS
    );
  }

  // Points the themes box at the given theme: its platforms, from the first.
  private applyActiveBanner( index: number ): void {
    this.activeThemeIndex = index;

    const next = this.PLUGIN_SCREENSHOTS[ index ];
    if ( !next ) {
      return;
    }

    this.selectedScreenshot = next;
    this.selectedPlatformIndex = 0;
    // Refresh cached fields once per theme change so the template can bind
    // to plain properties instead of invoking getters on every CD tick.
    this.visiblePlatforms = this.getVisiblePlatforms();
    this.selectedPlatformCached = this.visiblePlatforms[ this.selectedPlatformIndex ] ?? null;
    // Restart the platform counter from the start so each new theme shows
    // its first platform for a full interval before cycling.
    this.startPlatformRotation();
  }

  /** A footer link to a service (#services-<key>): the Our Services box shows that service (the page scrolls to
   *  its anchor by itself, product.service redirectToProductUrl). */
  onServiceFragment( fragment: string | null ): void {
    const prefix = SERVICES[ 0 ].key + '-';
    if ( !fragment?.startsWith( prefix ) ) {
      return;
    }
    const index = this.servicesShowcase.slides.findIndex( s => s.key === fragment.slice( prefix.length ) );
    if ( index >= 0 ) {
      // the box is in the view once the page has drawn it
      setTimeout( () => this.servicesBox?.pick( index ) );
    }
  }

  /**
   * The hero counter in the active theme's own colour that reads on the hero (owner, 2026-10-28): the most colourful
   * colour of the theme's circle with 3:1 on the stage (Aurora green, Sequoia light brown), Fuji its pink, then
   * darkened on a light stage or lightened on a dark one until it reaches 4.5:1. No theme: the site's theme colour.
   */
  private pickHeroCounterColor(): void {
    const el = this.heroCounterRef?.nativeElement;
    const stage = el?.closest( '.hero-stage' );
    if ( !el || !stage ) {
      return;
    }
    const fixed = HERO_COUNTER_FIXED[ this.heroThemeKey || '' ];
    if ( fixed ) {
      this.setHeroCounterColor( el, fixed );
      return;
    }
    const palette = ( this.heroPalette || [] ).filter( hex => /^#[0-9a-f]{6}$/i.test( hex ) );
    if ( !palette.length ) {
      el.style.removeProperty( '--hero-counter-color' );
      el.style.removeProperty( '--hero-counter-color-end' );
      return;
    }
    // the stage's fill (--codigrate-input-bg) from the theme's surfaces, else read through a probe without a
    // transition: the page reaches the new one only after the stage has faded to it
    let background = /^#[0-9a-f]{6}/i.test( this.heroInputBackground || '' ) ? this.heroInputBackground!.slice( 0, 7 ) : '';
    if ( !background ) {
      const probe = document.createElement( 'span' );
      probe.style.cssText = 'position:absolute;visibility:hidden;transition:none;color:var( --codigrate-input-bg )';
      stage.appendChild( probe );
      const rgb = getComputedStyle( probe ).color.match( /[\d.]+/g )?.map( Number ) || [ 255, 255, 255 ];
      probe.remove();
      // color(srgb 0..1) or rgb(0..255)
      const scale = rgb.slice( 0, 3 ).every( v => v <= 1 ) ? 255 : 1;
      background = '#' + rgb.slice( 0, 3 ).map( v => Math.round( v * scale ).toString( 16 ).padStart( 2, '0' ) ).join( '' );
    }
    const chroma = ( hex: string ) => {
      const rgb = [ 1, 3, 5 ].map( i => parseInt( hex.slice( i, i + 2 ), 16 ) );
      return Math.max( ...rgb ) - Math.min( ...rgb );
    };
    const contrast = ( hex: string ) => this.colorService.contrastRatio( hex, background );
    const readable = palette.filter( hex => contrast( hex ) >= 3 );
    const primary = this.heroThemeTokens?.primaryColor;
    const base = HERO_COUNTER_PRIMARY.includes( this.heroThemeKey || '' ) && primary ? primary
        : readable.length ? readable.reduce( ( best, hex ) => chroma( hex ) > chroma( best ) ? hex : best )
        : palette.reduce( ( best, hex ) => contrast( hex ) > contrast( best ) ? hex : best );
    // darker on a light stage, lighter on a dark one, step by step until it reads (4.5:1)
    const toward = this.colorService.relativeLuminance( background ) > 0.18 ? '#000000' : '#ffffff';
    let pick = base;
    for ( let t = 0.05; t <= 1 && this.colorService.contrastRatio( pick, background ) < 4.5; t += 0.05 ) {
      pick = this.colorService.interpolateColors( base, toward, t );
    }
    this.setHeroCounterColor( el, pick );
  }

  /** the counter's colour and its gradient's end, one tone lighter on every theme (owner, 2026-10-28) */
  private setHeroCounterColor( el: HTMLElement, hex: string ): void {
    el.style.setProperty( '--hero-counter-color', hex );
    el.style.setProperty( '--hero-counter-color-end', this.colorService.interpolateColors( hex, '#ffffff', 0.25 ) );
  }

  /** the pick once the theme's classes and surfaces have reached the page */
  private queueHeroCounterColor(): void {
    clearTimeout( this.heroCounterColorTimer );
    this.heroCounterColorTimer = setTimeout( () => {
      this.pickHeroCounterColor();
      // the fade only from the second colour on: the page opens on the theme's colour, not a fade into it
      setTimeout( () => this.heroCounterRef?.nativeElement.style.setProperty( '--hero-counter-fade', '0.6s' ), 50 );
    } );
  }

  ngOnDestroy(): void {
    this.modeSub?.unsubscribe();
    window.matchMedia( '(prefers-color-scheme: dark)' ).removeEventListener( 'change', this.systemSchemeListener );
    this.motifSub?.unsubscribe();
    this.surfaceSub?.unsubscribe();
    clearTimeout( this.heroCounterColorTimer );
    this.appearanceSub?.unsubscribe();
    this.fragmentSub?.unsubscribe();
    clearTimeout( this.circuitChargeCap );
    if ( this.themeBoxTimer ) {
      clearInterval( this.themeBoxTimer );
    }
    this.stopPlatformRotation();
    if ( this.downloadAnimationInterval ) {
      this.downloadAnimationInterval();
    }
    if ( this.smallScreenResizeListener ) {
      window.removeEventListener( 'resize', this.smallScreenResizeListener );
      this.smallScreenResizeListener = undefined;
    }
  }

  private initGlobe() {
    this.initImageGlobe();
  }

  private initImageGlobe() {
    if ( this.globe?.nativeElement ) {
      this.globe.nativeElement.innerHTML = '';
      const globe = document.createElement( 'img' );
      globe.src = './assets/product-lines/services/software-integration.webp';
      globe.style.height = this.utilService.isSmallScreen() ? '100vw' : '550px';
      globe.alt = 'globe';
      this.globe.nativeElement.appendChild( globe );
    }
  }


  /**
   * Angular change-detection hook. When the target platform screenshot's
   * `src` changes (theme rotation, platform click), kick off a preload
   * and only promote it to `displayedPlatformScreenshot` once the new
   * image is ready - keeps the slide animation from running against the
   * previous screenshot.
   */
  ngDoCheck(): void {
    this.checkDynamicDay();
    // Hot path - runs every CD tick. Use the cached field, not the getter
    // chain (selectedPlatform -> getVisiblePlatforms -> flattenPlatforms).
    const target = this.selectedPlatformCached?.screenshot ?? null;
    const src = target?.src ?? '';
    if ( src === this.lastTargetScreenshotSrc ) {
      return;
    }
    this.lastTargetScreenshotSrc = src;
    this.preloadAndCommitScreenshot( target );
  }

  private preloadAndCommitScreenshot( target: any ): void {
    if ( !target?.src ) {
      this.displayedPlatformScreenshot = target;
      return;
    }
    const src = target.src;
    const img = new window.Image();
    const commit = () => {
      // Drop the result if the visitor has already moved on - protects
      // against an in-flight preload clobbering a newer, faster one.
      if ( this.lastTargetScreenshotSrc !== src ) {
        return;
      }
      this.displayedPlatformScreenshot = target;
      target.loaded = true;
    };
    img.onload = commit;
    img.onerror = commit;
    img.src = src;
    if ( img.complete && img.naturalHeight > 0 ) {
      commit();
    }
  }

  getSrcWithDarkLightSetting( darkLightSetting: string, light: any, dark: any ) {
    switch ( darkLightSetting ) {
      case DarkLightSettings.light: {
        return light;
      }
      case DarkLightSettings.dark: {
        return dark;
      }
      case DarkLightSettings.auto:
      default: {
        return this.themeService.selectDarkLightByCSS( light, dark );
      }
    }
  }

  redirect( url: string ) {
    this.routingService.redirect( url );
  }

  /** Set once the visitor picks a theme or a platform: nothing turns by itself after that. */
  private themeBoxRotationStopped = false;

  /** Stops the themes box turning (themes and platforms) once the visitor acts in it, so what they
   *  act on stays put. Idempotent. */
  private stopThemeRotation(): void {
    this.themeBoxRotationStopped = true;
    if ( this.themeBoxTimer ) {
      clearInterval( this.themeBoxTimer );
      this.themeBoxTimer = undefined;
    }
    this.stopPlatformRotation();
  }

  // ---- the themes box (app-theme-showcase, owner 2026-10-10) ----
  private themeBoxTimer?: ReturnType<typeof setInterval>;
  /** The few themes the box turns through by itself: the newest, then the most downloaded. */
  private themeBoxRotation: string[] = [];

  /** A theme picked in the box: shown on the platform that was showing when the new theme has it
   *  (owner 2026-10-10: the platform is never reset to the first when it can stay), and the box
   *  stops turning. */
  pickTheme( key: string ): void {
    const index = this.PLUGIN_SCREENSHOTS.findIndex( t => t.product?.key === key );
    if ( index < 0 ) {
      return;
    }
    if ( index === this.activeThemeIndex ) {
      this.stopThemeRotation();
      return;
    }
    const platformName = this.selectedPlatformCached?.name;
    this.applyActiveBanner( index );
    this.stopThemeRotation();
    const same = platformName ? this.visiblePlatforms.findIndex( p => p.name === platformName ) : -1;
    if ( same > 0 ) {
      this.selectedPlatformIndex = same;
      this.selectedPlatformCached = this.visiblePlatforms[ same ];
    }
  }

  /** The newest theme first, then the five most downloaded (the five newest until the stats come). */
  private refreshThemeBoxRotation(): void {
    const themes = this.PLUGIN_SCREENSHOTS.map( t => t.product ).filter( Boolean );
    const byDate = [ ...themes ].sort( ( a, b ) => ( b.releaseDate || '' ).localeCompare( a.releaseDate || '' ) );
    const newest = byDate[ 0 ]?.key;
    const rest = this.pluginDownloadsLoaded
      ? [ ...themes ].filter( p => p.key !== newest ).sort( ( a, b ) => ( b.totalDownloads || 0 ) - ( a.totalDownloads || 0 ) )
      : byDate.slice( 1 );
    this.themeBoxRotation = [ newest, ...rest.slice( 0, 5 ).map( p => p.key ) ].filter( Boolean );
  }

  /** True while the box asks to hold (pointer or focus in it, or it is off screen). */
  private themeBoxHeld = true;

  holdThemeBox( held: boolean ): void {
    this.themeBoxHeld = held;
  }

  /** Under Reduce Motion nothing in the box turns by itself. */
  private get reducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.( '(prefers-reduced-motion: reduce)' ).matches;
  }

  private startThemeBoxRotation(): void {
    if ( this.themeBoxTimer ) {
      clearInterval( this.themeBoxTimer );
    }
    if ( this.reducedMotion ) {
      return;
    }
    this.themeBoxTimer = setInterval( () => {
      if ( ( typeof document !== 'undefined' && document.hidden ) || this.themeBoxHeld ) {
        return;
      }
      const current = this.selectedScreenshot?.product?.key;
      const at = this.themeBoxRotation.indexOf( current );
      const next = this.themeBoxRotation[ ( at + 1 ) % this.themeBoxRotation.length ];
      const index = this.PLUGIN_SCREENSHOTS.findIndex( t => t.product?.key === next );
      if ( index >= 0 ) {
        this.applyActiveBanner( index );
      }
    }, this.animationInterval );
  }

  /** Start cycling the active theme's platform screenshots every
   * `PLATFORM_ROTATION_MS`, mirroring the theme page carousel. Idempotent. */
  private startPlatformRotation(): void {
    this.stopPlatformRotation();
    // The themes box renders on phones too, so its platforms cycle there as well (2026-10-10).
    // While the visitor has picked something (the theme rotation stopped), it stays put.
    if ( this.themeBoxRotationStopped || this.reducedMotion ) {
      return;
    }
    this.platformRotationInterval = setInterval( () => this.rotatePlatform(), this.PLATFORM_ROTATION_MS );
  }

  private stopPlatformRotation(): void {
    if ( this.platformRotationInterval ) {
      clearInterval( this.platformRotationInterval );
      this.platformRotationInterval = undefined;
    }
  }

  /** Advance to the next platform of the current theme. The theme rotation
   * resets the index to 0 on each theme change, so this just steps within the
   * active theme's platforms (no-op while a theme has 0-1 platforms). */
  private rotatePlatform(): void {
    if ( ( typeof document !== 'undefined' && document.hidden ) || this.themeBoxHeld ) {
      return;
    }
    const platforms = this.getVisiblePlatforms();
    const count = platforms.length;
    if ( count <= 1 ) {
      return;
    }
    this.selectedPlatformIndex = ( this.selectedPlatformIndex + 1 ) % count;
    this.selectedPlatformCached = platforms[ this.selectedPlatformIndex ] ?? null;
  }

  onPlatformClick( platformName: string, url: string, downloadable = false ) {
    // a button in the themes box: keep the current screenshot
    this.stopThemeRotation();
    this.monitorProviderService.recordClick( this.selectedScreenshot.product.key, platformName ).subscribe( {
      error: ( err ) => console.error( 'Failed to record platform click:', err )
    } );

    if ( downloadable ) {
      // the free listing outside the cart (2026-09-21, no theme/content page any more): Rhino
      const product = this.selectedScreenshot.product;
      if ( platformCtaKind( product, platformName ) === 'download' ) this.directDownloadService.downloadRhino( product, platformName, url );
      return;
    }

    window.open( url, '_blank' );
  }

  // Cached flattened platforms, so the themes box's [platforms] input keeps one array reference
  // until the theme changes.
  private _platformsCache: {
    key: string,
    list: FlatPlatform[]
  } | null = null;

  /** Flattens the selected theme's platforms (incl. supportedPlatforms) and
   * returns ALL platforms. Returns the same array reference until
   * product.key changes. */
  getVisiblePlatforms(): FlatPlatform[] {
    const key = this.selectedScreenshot?.product?.key ?? '';
    if ( this._platformsCache && this._platformsCache.key === key ) {
      return this._platformsCache.list;
    }
    this._platformsCache = { key, list: this.flattenPlatforms() };
    return this._platformsCache.list;
  }

  /** Walks the selected product's platforms one level deep into
   * supportedPlatforms so each IDE/editor variant becomes its own pill.
   * Each flat entry carries the platform's own screenshot. */
  private flattenPlatforms(): FlatPlatform[] {
    const out: FlatPlatform[] = [];
    const product = this.selectedScreenshot?.product;
    const platforms: any[] = product?.platforms;
    if ( !platforms ) {
      return out;
    }
    for ( const platform of sortPlatformsByCoverage( platforms ) ) {
      const info: any = platform.info;
      const screenshot = platform.screenshot;
      if ( info?.supportedPlatforms?.length > 0 ) {
        for ( const sp of info.supportedPlatforms ) {
          out.push( {
            name: sp.name,
            logo: sp.logo,
            url: platform.url,
            downloadable: info.downloadable,
            screenshot,
            parentName: info.name,
            category: sp.category ?? info.category,
          } );
        }
      } else if ( info ) {
        out.push( {
          name: info.name,
          logo: info.logo,
          url: platform.url,
          downloadable: info.downloadable,
          screenshot,
          category: info.category,
        } );
      }
    }
    return out;
  }

  /** Currently-selected flat platform (or null if none). */
  get selectedPlatform(): FlatPlatform | null {
    return this.getVisiblePlatforms()[this.selectedPlatformIndex] ?? null;
  }

  selectPlatform( idx: number ): void {
    this.stopThemeRotation();
    if ( this.selectedPlatformIndex === idx ) {
      return;
    }
    this.selectedPlatformIndex = idx;
    this.selectedPlatformCached = this.visiblePlatforms[ idx ] ?? null;
  }

  tryNowSelected(): void {
    const plat = this.selectedPlatform;
    if ( !plat ) {
      return;
    }
    // A listing sold on the site: Add to cart puts it in the cart and opens
    // the mini cart (a toast on a phone); the page stays.
    const sku = platformSku( this.selectedScreenshot?.product, plat.name );
    if ( sku ) {
      this.stopThemeRotation();
      this.cartPopupService.add( sku, 'home' );
      this.monitorProviderService.recordClick( this.selectedScreenshot.product.key, plat.name ).subscribe( {
        error: ( err ) => console.error( 'Failed to record platform click:', err )
      } );
      return;
    }
    this.onPlatformClick( plat.name, plat.url, plat.downloadable );
  }
}
